import type { IntegrationServices } from '@/modules/common/types/integrations';
import { OpsiumUserShort } from '@/modules/common/types/opsium-user';

import { BillTimeOffSource } from './generate-bills';

export enum BillStatus {
  Open = 'open',
  Paid = 'paid',
  Overdue = 'overdue',
  InProgress = 'inProgress',
  Error = 'error',
  Unknown = 'unknown',
}

export interface BillSyncError {
  code: number;
  message: string;
  provider: IntegrationServices | null;
  details: string[] | null;
  reason: BillSyncErrorReason | null;
}

export enum BillSyncOperation {
  Create = 'CREATE',
  Update = 'UPDATE',
}

export enum BillSyncErrorReason {
  VendorNotAvailable = 'VENDOR_NOT_AVAILABLE',
  ProjectNotAvailable = 'PROJECT_NOT_AVAILABLE',
  ProductOrServiceNotAvailable = 'PRODUCT_OR_SERVICE_NOT_AVAILABLE',
  VendorCurrencyMismatch = 'VENDOR_CURRENCY_MISMATCH',
  CurrencyNotEnabled = 'CURRENCY_NOT_ENABLED',
  AccountingPeriodClosed = 'ACCOUNTING_PERIOD_CLOSED',
  NegativeLineAmountNotAllowed = 'NEGATIVE_LINE_AMOUNT_NOT_ALLOWED',
  LineDescriptionTooLong = 'LINE_DESCRIPTION_TOO_LONG',
  TransactionDateNotAllowed = 'TRANSACTION_DATE_NOT_ALLOWED',
  LineValueOutOfRange = 'LINE_VALUE_OUT_OF_RANGE',
  DataRejected = 'DATA_REJECTED',
}

export enum BillSyncStatus {
  Scheduled = 'SCHEDULED',
  InProgress = 'IN_PROGRESS',
  Synchronized = 'SYNCHRONIZED',
  Failed = 'FAILED',
}

export enum BillAllowedAction {
  Edit = 'EDIT',
  Delete = 'DELETE',
  Retry = 'RETRY',
}

export enum BillDetailsItemType {
  Project = 'PROJECT',
  Client = 'CLIENT',
  Unlinked = 'NONE',
  TimeOffType = 'TIME_OFF_TYPE',
  Overhead = 'OVERHEAD',
  Salary = 'SALARY',
  Adjustment = 'ADJUSTMENT',
}

export interface BillDetailsItem {
  id: number;
  entityType: BillDetailsItemType;
  entityId: number | null;
  name: string | null;
  avatarUrl: string | null;
  colorHex: string | null;
  description: string | null;
  quantity: string | null;
  unitPrice: string | null;
  amount: string;
}

export interface BillMonthlySalaryAvailability {
  isAvailable: boolean;
  monthlySalaryAmount: string | null;
}

export interface BillDetailsTimeOffTypeSource {
  timeOffTypeId: number;
  timeOffTypeName: string;
  isBillable: boolean;
  hoursSources: BillTimeOffSource[];
}

/** Domain model consumed outside the bills API and mapper layers. */
export interface BillDetails {
  id: number;
  externalId: string | null;
  referenceNumber: string | null;
  status: BillStatus;
  syncStatus: BillSyncStatus;
  syncOperation: BillSyncOperation | null;
  syncError: BillSyncError | null;
  syncToken: string;
  allowedActions: BillAllowedAction[];
  member: OpsiumUserShort;
  startDate: string | null;
  endDate: string | null;
  dueDate: string;
  currencyCode: string;
  totalAmount: string;
  balance: string;
  amountPaid: string;
  monthlySalaryAvailability: BillMonthlySalaryAvailability;
  timeOffSources: BillDetailsTimeOffTypeSource[];
  createdAt: string;
  items: BillDetailsItem[];
}

export interface BillListItem {
  id: number;
  /** Unique bill number from QuickBooks */
  number: string | null;
  externalId: string | null;
  member: OpsiumUserShort;
  status: BillStatus;
  createdOn: Date;
  /** Formatted billing period (e.g., "Jan 1 - Jan 31, 2025") */
  period: string;
  dueDate: Date;
  balance: string;
  amount: string;
  currencyCode: string;
  syncError: BillSyncError | null;
}
